/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import type { CbAugmentedRoutes } from "./@classes/routes/cb-routes-utils";
import { AuthGuard } from "./guards/auth.guard";
import { CreateTenantGuard } from "./guards/create-tenant.guard";
import { palmtopCompanyGuard } from "./guards/palmtop-company.guard";
import { palmtopWarehouseGuard } from "./guards/palmtop-warehouse.guard";
import { SuperadminAuthGuard } from "./guards/superadminAuth.guard";
import { TenantAuthGuard } from "./guards/tenantAuth.guard";

export const routes: CbAugmentedRoutes = {
	notFound: {
		path: "**",
		loadComponent: () =>
			import(
				"./modules/common/views/not-found-view/not-found-view.component"
			).then((m) => m.NotFoundViewComponent),
	},
	root: {
		redirectTo: "choose_office",
		pathMatch: "full",
	},
	auth: {
		showSidebar: false,
		children: {
			root: {
				redirectTo: "login",
				pathMatch: "full",
			},
			login: {
				loadComponent: () =>
					import(
						"./modules/authentication/views/login-view/login-view.component"
					).then((m) => m.LoginViewComponent),
			},
			signup: {
				loadComponent: () =>
					import(
						"./modules/authentication/views/signup-view/signup-view.component"
					).then((m) => m.SignupViewComponent),
			},
			invitation: {
				children: {
					root: {
						redirectTo: "chooose_office",
						pathMatch: "full",
					},
					token: {
						path: ":token",
						loadComponent: () =>
							import(
								"./modules/authentication/views/invitation-view/invitation-view.component"
							).then((m) => m.InvitationViewComponent),
					},
				},
			},
			forgotPassword: {
				loadComponent: () =>
					import(
						"./modules/authentication/views/forgot-password-view/forgot-password-view.component"
					).then((m) => m.ForgotPasswordViewComponent),
			},
			confirmAccount: {
				loadComponent: () =>
					import(
						"./modules/authentication/views/confirm-account-view/confirm-account-view.component"
					).then((m) => m.ConfirmAccountViewComponent),
			},
		},
	},
	tenant: {
		showSidebar: false,
		canActivate: [AuthGuard],
		children: {
			root: {
				pathMatch: "full",
				loadComponent: () =>
					import(
						"./modules/tenant/views/select-tenant-view/select-tenant-view.component"
					).then((m) => m.SelectTenantViewComponent),
			},
			onboarding: {
				loadComponent: () =>
					import(
						"./modules/tenant/views/first-onboarding-view/first-onboarding-view.component"
					).then((m) => m.FirstOnboardingViewComponent),
			},
			invitations: {
				loadComponent: () =>
					import(
						"./modules/tenant/views/tenant-invitations-view/tenant-invitations-view.component"
					).then((m) => m.TenantInvitationsViewComponent),
			},
			create: {
				canActivate: [CreateTenantGuard],
				loadComponent: () =>
					import(
						"./modules/tenant/views/create-tenant-view/create-tenant-view.component"
					).then((m) => m.CreateTenantViewComponent),
			},
			id: {
				path: ":id",
				loadComponent: () =>
					import(
						"./modules/tenant/views/select-tenant-view/select-tenant-view.component"
					).then((m) => m.SelectTenantViewComponent),
			},
		},
	},
	choose_office: {
		showSidebar: false,
		canActivate: [AuthGuard, TenantAuthGuard],
		loadComponent: () =>
			import(
				"./modules/choose_office/choose-office/choose-office.component"
			).then((m) => m.ChooseOfficeComponent),
	},
	backoffice: {
		showSidebar: true,
		canActivate: [AuthGuard, SuperadminAuthGuard],
		data: { backoffice: true },
		children: {
			root: {
				redirectTo: "tenants",
				pathMatch: "full",
			},
			tenants: {
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/backoffice/views/manage-tenants-list/manage-tenants-list.component"
							).then((m) => m.ManageTenantsListComponent),
					},
					id: {
						path: ":id",
						loadComponent: () =>
							import(
								"./modules/backoffice/views/single-tenant-view/single-tenant-view.component"
							).then((m) => m.SingleTenantViewComponent),
					},
				},
			},
			users: {
				loadComponent: () =>
					import(
						"./modules/backoffice/views/manage-users-list/manage-users-list.component"
					).then((m) => m.ManageUsersListComponent),
			},
			ai: {
				loadComponent: () =>
					import(
						"./modules/backoffice/views/ai-panel-view/ai-panel-view.component"
					).then((m) => m.AiPanelViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "usage",
					},
					usage: {
						loadComponent: () =>
							import(
								"./modules/backoffice/views/ai-usage-list/ai-usage-list.component"
							).then((m) => m.AiUsageListComponent),
					},
					models: {
						loadComponent: () =>
							import(
								"./modules/backoffice/views/model-prices-view/model-prices-view.component"
							).then((m) => m.ModelPricesViewComponent),
					},
					statistics: {
						loadComponent: () =>
							import(
								"./modules/backoffice/views/ai-statistics-view/ai-statistics-view.component"
							).then((m) => m.AiStatisticsViewComponent),
					},
				},
			},
		},
	},
	w: {
		showSidebar: true,
		canActivate: [AuthGuard, TenantAuthGuard],
		data: { office: "warehouse" },
		children: {
			root: {
				redirectTo: "home",
				pathMatch: "full",
			},
			home: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/home-view/home-view.component"
							).then((m) => m.HomeViewComponent),
					},
				},
			},
			settings: {
				showSidebar: true,
				features: ["settings"],
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/settings/views/settings-view/settings-view.component"
					).then((m) => m.SettingsViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "user",
					},
					tenant: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/tenant-settings-view/tenant-settings-view.component"
							).then((m) => m.TenantSettingsViewComponent),
					},
					user: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/user-settings-view/user-settings-view.component"
							).then((m) => m.UserSettingsViewComponent),
					},
					collaborators: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/collaborators-settings-view/collaborators-settings-view.component"
							).then((m) => m.CollaboratorsSettingsViewComponent),
					},
					email_settings: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/email-settings-view/email-settings-view.component"
							).then((m) => m.EmailSettingsViewComponent),
					},
					price_list: {
						realtimeCollaboration: false,
						children: {
							root: {
								pathMatch: "full",
								redirectTo: "list",
							},
							list: {
								loadComponent: () =>
									import(
										"./modules/settings/subviews/warehouse-price-list-settings-view/warehouse-price-list-settings-view.component"
									).then((m) => m.WarehousePriceListSettingsViewComponent),
							},
							create: {
								loadComponent: () =>
									import(
										"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
									).then((m) => m.CreateEditPriceListComponent),
							},
							edit: {
								path: ":id/edit",
								loadComponent: () =>
									import(
										"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
									).then((m) => m.CreateEditPriceListComponent),
							},
						},
					},
					tags: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/tags-settings-view/tags-settings-view.component"
							).then((m) => m.TagsSettingsViewComponent),

						children: {
							root: {
								pathMatch: "full",
								redirectTo: "orders_tags",
							},
							orders_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/orders-tags-settings-view/orders-tags-settings-view.component"
									).then((m) => m.OrdersTagsSettingsViewComponent),
							},
							pallets_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/pallets-tags-settings-view/pallets-tags-settings-view.component"
									).then((m) => m.PalletsTagsSettingsViewComponent),
							},
							articles_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/articles-tags-settings-view/articles-tags-settings-view.component"
									).then((m) => m.ArticlesTagsSettingsViewComponent),
							},
						},
					},
				},
			},
			products: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/products/views/articles-list-view/articles-list-view.component"
							).then((m) => m.ArticlesListViewComponent),
					},
				},
			},
			warehouse: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "management",
					},
					management: {
						children: {
							root: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/warehouse/views/warehouse-management-view/warehouse-management-view.component"
									).then((m) => m.WarehouseManagementViewComponent),
							},
							create: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/warehouse/views/create-edit-pallet-view/create-edit-pallet-view.component"
									).then((m) => m.CreateEditPalletViewComponent),
							},
							id: {
								path: ":id/edit",
								loadComponent: () =>
									import(
										"./modules/warehouse_office/warehouse/views/create-edit-pallet-view/create-edit-pallet-view.component"
									).then((m) => m.CreateEditPalletViewComponent),
							},
						},
					},
					layout: {
						loadComponent: () =>
							import(
								"./modules/warehouse_office/warehouse/views/warehouse-layout-view/warehouse-layout-view.component"
							).then((m) => m.WarehouseLayoutViewComponent),
					},
				},
			},
			orders: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "incoming",
					},
					incoming: {
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/orders/views/incoming-orders-view/incoming-orders-view.component"
									).then((m) => m.IncomingOrdersViewComponent),
							},
							create: {
								loadComponent: () =>
									import(
										"./modules/orders/views/create-edit-incoming-order-view/create-edit-incoming-order-view.component"
									).then((m) => m.CreateEditIncomingOrderViewComponent),
							},
							id: {
								path: ":id",
								children: {
									edit: {
										loadComponent: () =>
											import(
												"./modules/orders/views/create-edit-incoming-order-view/create-edit-incoming-order-view.component"
											).then((m) => m.CreateEditIncomingOrderViewComponent),
									},
								},
							},
						},
					},
					outgoing: {
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/orders/views/outgoing-orders-view/outgoing-orders-view.component"
									).then((m) => m.OutgoingOrdersViewComponent),
							},
							create: {
								loadComponent: () =>
									import(
										"./modules/orders/views/create-edit-outgoing-order-view/create-edit-outgoing-order-view.component"
									).then((m) => m.CreateEditOutgoingOrderViewComponent),
							},
							id: {
								path: ":id",
								children: {
									edit: {
										loadComponent: () =>
											import(
												"./modules/orders/views/create-edit-outgoing-order-view/create-edit-outgoing-order-view.component"
											).then((m) => m.CreateEditOutgoingOrderViewComponent),
									},
								},
							},
						},
					},
				},
			},
			companies: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/companies/views/companies-list-view/companies-list-view.component"
							).then((m) => m.CompaniesListViewComponent),
					},
					create: {
						loadComponent: () =>
							import(
								"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
							).then((m) => m.CreateEditCompanyViewComponent),
					},
					id: {
						path: ":id",
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/companies/views/single-company-view/single-company-view.component"
									).then((m) => m.SingleCompanyViewComponent),
							},
							edit: {
								loadComponent: () =>
									import(
										"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
									).then((m) => m.CreateEditCompanyViewComponent),
							},
						},
					},
				},
			},
			works: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/works/views/works-list-view/works-list-view.component"
							).then((m) => m.WorksListViewComponent),
					},
				},
			},
			inventory: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/inventory/views/inventories-list-view/inventories-list-view.component"
							).then((m) => m.InventoriesListViewComponent),
					},
					id: {
						path: ":id",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/inventory/views/single-inventory-view/single-inventory-view.component"
							).then((m) => m.SingleInventoryViewComponent),
					},
				},
			},
			alerts: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/alerts/views/alerts-list-view/alerts-list-view.component"
							).then((m) => m.AlertsListViewComponent),
					},
				},
			},
			palmtop_operations: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/palmtop-operations/views/palmtop-operations-list-view/palmtop-operations-list-view.component"
							).then((m) => m.PalmtopOperationsListViewComponent),
					},
				},
			},
			email: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/email/views/emails-main-view/emails-main-view.component"
					).then((m) => m.EmailsMainViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "INBOX",
					},
					mailbox: {
						path: ":mailbox",
						loadComponent: () =>
							import(
								"./modules/email/views/email-mailbox-view/email-mailbox-view.component"
							).then((m) => m.EmailMailboxViewComponent),
						children: {
							id: {
								path: ":id",
								loadComponent: () =>
									import(
										"./modules/email/views/single-email-view/single-email-view.component"
									).then((m) => m.SingleEmailViewComponent),
							},
						},
					},
				},
			},

			palmtop_choose_warehouse: {
				showSidebar: false,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/warehouse_office/palmtop/home/views/choose-warehouse-palmtop/choose-warehouse-palmtop.component"
					).then((m) => m.ChooseWarehousePalmtopComponent),
			},
			palmtop_choose_client: {
				showSidebar: false,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard, palmtopWarehouseGuard],
				loadComponent: () =>
					import(
						"./modules/warehouse_office/palmtop/home/views/choose-client-palmtop/choose-client-palmtop.component"
					).then((m) => m.ChooseClientPalmtopComponent),
			},
			palmtop: {
				showSidebar: false,
				realtimeCollaboration: true,
				canActivate: [
					AuthGuard,
					TenantAuthGuard,
					palmtopWarehouseGuard,
					palmtopCompanyGuard,
				],
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "home",
					},
					home: {
						loadComponent: () =>
							import(
								"./modules/warehouse_office/palmtop/home/views/home-palmtop-view/home-palmtop-view.component"
							).then((m) => m.HomePalmtopViewComponent),
					},

					order_entry: {
						children: {
							root: {
								pathMatch: "full",
								redirectTo: "list",
							},
							list: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/order-entry/views/order-entry-view/order-entry-view.component"
									).then((m) => m.OrderEntryViewComponent),
							},
							id: {
								path: ":id",
								children: {
									root: {
										loadComponent: () =>
											import(
												"./modules/warehouse_office/palmtop/order-entry/views/single-incoming-order-view/single-incoming-order-view.component"
											).then((m) => m.SingleIncomingOrderViewComponent),
									},
									scan: {
										loadComponent: () =>
											import(
												"./modules/warehouse_office/palmtop/order-entry/views/scan-incoming-product/scan-incoming-product.component"
											).then((m) => m.ScanIncomingProductComponent),
									},
								},
							},
						},
					},
					outgoing_order: {
						children: {
							root: {
								pathMatch: "full",
								redirectTo: "list",
							},
							list: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/outgoing-order/views/outgoing-orders-palmtop-list-view/outgoing-orders-palmtop-list-view.component"
									).then((m) => m.OutgoingOrdersPalmtopListViewComponent),
							},
							id: {
								path: ":id",
								children: {
									root: {
										loadComponent: () =>
											import(
												"./modules/warehouse_office/palmtop/outgoing-order/views/outgoing-order-palmtop-single-view/outgoing-order-palmtop-single-view.component"
											).then((m) => m.OutgoingOrderPalmtopSingleViewComponent),
									},
									scan: {
										loadComponent: () =>
											import(
												"./modules/warehouse_office/palmtop/outgoing-order/views/outgoing-order-palmtop-scan-view/outgoing-order-palmtop-scan-view.component"
											).then((m) => m.OutgoingOrderPalmtopScanViewComponent),
									},
								},
							},
						},
					},
					move: {
						loadComponent: () =>
							import(
								"./modules/warehouse_office/palmtop/move/views/move-palmtop-view/move-palmtop-view.component"
							).then((m) => m.MovePalmtopViewComponent),
					},
					search: {
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/search/views/search-view-component/search-view-component.component"
									).then((m) => m.SearchViewComponentComponent),
							},
							udc: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/search/views/find-udc-view/find-udc-view.component"
									).then((m) => m.FindUdcViewComponent),
							},
							article: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/search/views/find-article-view/find-article-view.component"
									).then((m) => m.FindArticleViewComponent),
							},
							location: {
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/search/views/find-shelf-content-view/find-shelf-content-view.component"
									).then((m) => m.FindShelfContentViewComponent),
							},
						},
					},
					inventary: {
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/warehouse_office/palmtop/inventory/views/palmtop-inventory-view/palmtop-inventory-view.component"
									).then((m) => m.PalmtopInventoryViewComponent),
							},
							id: {
								path: ":id",
								children: {
									root: {
										pathMatch: "full",
										redirectTo: "advancement",
									},
									advancement: {
										children: {
											root: {
												loadComponent: () =>
													import(
														"./modules/warehouse_office/palmtop/inventory/views/palmtop-inventory-advancement-view/palmtop-inventory-advancement-view.component"
													).then(
														(m) => m.PalmtopInventoryAdvancementViewComponent,
													),
											},
											shelf_area_id: {
												path: ":shelf_area_id",
												loadComponent: () =>
													import(
														"./modules/warehouse_office/palmtop/inventory/views/palmtop-inventory-step/palmtop-inventory-step.component"
													).then((m) => m.PalmtopInventoryStepComponent),
											},
										},
									},
								},
							},
						},
					},
					create_udc: {
						loadComponent: () =>
							import(
								"./modules/warehouse_office/palmtop/create-udc/views/palmtop-create-udc/palmtop-create-udc.component"
							).then((m) => m.PalmtopCreateUdcComponent),
					},
				},
			},
		},
	},
	a: {
		showSidebar: true,
		canActivate: [AuthGuard, TenantAuthGuard],
		data: { office: "administration" },
		children: {
			root: {
				redirectTo: "home",
				pathMatch: "full",
			},
			home: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/home-view/home-view.component"
							).then((m) => m.HomeViewComponent),
					},
				},
			},
			settings: {
				showSidebar: true,
				features: ["settings"],
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/settings/views/settings-view/settings-view.component"
					).then((m) => m.SettingsViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "user",
					},
					tenant: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/tenant-settings-view/tenant-settings-view.component"
							).then((m) => m.TenantSettingsViewComponent),
					},
					user: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/user-settings-view/user-settings-view.component"
							).then((m) => m.UserSettingsViewComponent),
					},
					collaborators: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/collaborators-settings-view/collaborators-settings-view.component"
							).then((m) => m.CollaboratorsSettingsViewComponent),
					},
					invoice_settings: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/administration_office/invoices_module/settings/sections/invoice-settings-view/invoice-settings-view.component"
							).then((m) => m.InvoiceSettingsViewComponent),

						children: {
							root: {
								pathMatch: "full",
								redirectTo: "payment_conditions",
							},
							payment_conditions: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/payment-conditions-view/payment-conditions-view.component"
									).then((m) => m.PaymentConditionsViewComponent),
							},
							vat_codes: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/vat-codes-list-view/vat-codes-list-view.component"
									).then((m) => m.VatCodesListViewComponent),
							},
							measure_units: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/measure-units-list-view/measure-units-list-view.component"
									).then((m) => m.MeasureUnitsListViewComponent),
							},
							tenant_invoice_parameters: {
								realtimeCollaboration: true,
								children: {
									root: {
										pathMatch: "full",
										loadComponent: () =>
											import(
												"./modules/administration_office/invoices_module/settings/views/tenant-invoice-parameters-list-view/tenant-invoice-parameters-list-view.component"
											).then((m) => m.TenantInvoiceParametersListViewComponent),
									},
									create: {
										loadComponent: () =>
											import(
												"./modules/administration_office/invoices_module/settings/views/create-edit-invoice-parameter-view/create-edit-invoice-parameter-view.component"
											).then((m) => m.CreateEditInvoiceParameterViewComponent),
									},
									id: {
										path: ":id",
										children: {
											root: {
												pathMatch: "full",
												redirectTo: "edit",
											},
											edit: {
												loadComponent: () =>
													import(
														"./modules/administration_office/invoices_module/settings/views/create-edit-invoice-parameter-view/create-edit-invoice-parameter-view.component"
													).then(
														(m) => m.CreateEditInvoiceParameterViewComponent,
													),
											},
										},
									},
								},
							},
							document_types: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/document-types-list-view/document-types-list-view.component"
									).then((m) => m.DocumentTypesListViewComponent),
							},
							bank_cash: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/bank-cash-list-view/bank-cash-list-view.component"
									).then((m) => m.BankCashListViewComponent),
							},
							invoice_server_settings: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/settings/views/invoice-server-settings-view/invoice-server-settings-view.component"
									).then((m) => m.InvoiceServerSettingsViewComponent),
							},
						},
					},
				},
			},
			companies: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/companies/views/companies-list-view/companies-list-view.component"
							).then((m) => m.CompaniesListViewComponent),
					},
					create: {
						loadComponent: () =>
							import(
								"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
							).then((m) => m.CreateEditCompanyViewComponent),
					},
					id: {
						path: ":id",
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/companies/views/single-company-view/single-company-view.component"
									).then((m) => m.SingleCompanyViewComponent),
							},
							edit: {
								loadComponent: () =>
									import(
										"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
									).then((m) => m.CreateEditCompanyViewComponent),
							},
						},
					},
				},
			},
			vehicles: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/administration_office/vehicles/views/vehicles-list-view/vehicles-list-view.component"
							).then((m) => m.VehiclesListViewComponent),
					},
					id: {
						path: ":id",
						loadComponent: () =>
							import(
								"./modules/administration_office/vehicles/views/single-vehicle-view/single-vehicle-view.component"
							).then((m) => m.SingleVehicleViewComponent),
					},
				},
			},
			staff_members: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/administration_office/staff-members/views/staff-members-list-view/staff-members-list-view.component"
							).then((m) => m.StaffMembersListViewComponent),
					},
					id: {
						path: ":id",
						loadComponent: () =>
							import(
								"./modules/administration_office/staff-members/views/single-staff-member-view/single-staff-member-view.component"
							).then((m) => m.SingleStaffMemberViewComponent),
					},
				},
			},
			invoices: {
				showSidebar: true,
				realtimeCollaboration: true,
				features: ["invoicing"],
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "list",
					},
					list: {
						loadComponent: () =>
							import(
								"./modules/administration_office/invoices_module/invoices/sections/invoices-section-view/invoices-section-view.component"
							).then((m) => m.InvoicesSectionViewComponent),
					},
					create: {
						loadComponent: () =>
							import(
								"./modules/administration_office/invoices_module/invoices/views/create-edit-sale-invoice/create-edit-sale-invoice.component"
							).then((m) => m.CreateEditSaleInvoiceComponent),
					},
					id: {
						path: ":id",
						children: {
							root: {
								pathMatch: "full",
								redirectTo: "edit",
							},
							edit: {
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/invoices/views/create-edit-sale-invoice/create-edit-sale-invoice.component"
									).then((m) => m.CreateEditSaleInvoiceComponent),
							},
							view_sale: {
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/invoices/views/invoice-single-view/invoice-single-view.component"
									).then((m) => m.InvoiceSingleViewComponent),
							},
							view_purchase: {
								loadComponent: () =>
									import(
										"./modules/administration_office/invoices_module/invoices/views/create-edit-sale-invoice/create-edit-sale-invoice.component"
									).then((m) => m.CreateEditSaleInvoiceComponent),
							},
						},
					},
				},
			},
			calendar: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/administration_office/calendar/views/calendar-view/calendar-view.component"
					).then((m) => m.CalendarViewComponent),
			},
			pre_billing: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/administration_office/pre-billing/views/pre-billing-view/pre-billing-view.component"
					).then((m) => m.PreBillingViewComponent),
			},
			email: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/email/views/emails-main-view/emails-main-view.component"
					).then((m) => m.EmailsMainViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "INBOX",
					},
					mailbox: {
						path: ":mailbox",
						loadComponent: () =>
							import(
								"./modules/email/views/email-mailbox-view/email-mailbox-view.component"
							).then((m) => m.EmailMailboxViewComponent),
						children: {
							id: {
								path: ":id",
								loadComponent: () =>
									import(
										"./modules/email/views/single-email-view/single-email-view.component"
									).then((m) => m.SingleEmailViewComponent),
							},
						},
					},
				},
			},
		},
	},
	p: {
		showSidebar: true,
		canActivate: [AuthGuard, TenantAuthGuard],
		data: { office: "planning" },
		children: {
			root: {
				redirectTo: "home",
				pathMatch: "full",
			},
			home: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/warehouse_office/home-view/home-view.component"
							).then((m) => m.HomeViewComponent),
					},
				},
			},
			companies: {
				showSidebar: true,
				realtimeCollaboration: true,
				canActivate: [AuthGuard, TenantAuthGuard],
				children: {
					root: {
						pathMatch: "full",
						loadComponent: () =>
							import(
								"./modules/companies/views/companies-list-view/companies-list-view.component"
							).then((m) => m.CompaniesListViewComponent),
					},
					create: {
						loadComponent: () =>
							import(
								"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
							).then((m) => m.CreateEditCompanyViewComponent),
					},
					id: {
						path: ":id",
						children: {
							root: {
								pathMatch: "full",
								loadComponent: () =>
									import(
										"./modules/companies/views/single-company-view/single-company-view.component"
									).then((m) => m.SingleCompanyViewComponent),
							},
							edit: {
								loadComponent: () =>
									import(
										"./modules/companies/views/create-edit-company-view/create-edit-company-view.component"
									).then((m) => m.CreateEditCompanyViewComponent),
							},
						},
					},
				},
			},
			settings: {
				showSidebar: true,
				features: ["settings"],
				canActivate: [AuthGuard, TenantAuthGuard],
				loadComponent: () =>
					import(
						"./modules/settings/views/settings-view/settings-view.component"
					).then((m) => m.SettingsViewComponent),
				children: {
					root: {
						pathMatch: "full",
						redirectTo: "user",
					},
					tenant: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/tenant-settings-view/tenant-settings-view.component"
							).then((m) => m.TenantSettingsViewComponent),
					},
					user: {
						realtimeCollaboration: false,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/user-settings-view/user-settings-view.component"
							).then((m) => m.UserSettingsViewComponent),
					},
					collaborators: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/collaborators-settings-view/collaborators-settings-view.component"
							).then((m) => m.CollaboratorsSettingsViewComponent),
					},
					price_list: {
						realtimeCollaboration: false,
						children: {
							root: {
								pathMatch: "full",
								redirectTo: "list",
							},
							list: {
								loadComponent: () =>
									import(
										"./modules/settings/subviews/warehouse-price-list-settings-view/warehouse-price-list-settings-view.component"
									).then((m) => m.WarehousePriceListSettingsViewComponent),
							},
							create: {
								loadComponent: () =>
									import(
										"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
									).then((m) => m.CreateEditPriceListComponent),
							},
							edit: {
								path: ":id/edit",
								loadComponent: () =>
									import(
										"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
									).then((m) => m.CreateEditPriceListComponent),
							},
						},
					},
					tags: {
						realtimeCollaboration: true,
						loadComponent: () =>
							import(
								"./modules/settings/subviews/tags-settings-view/tags-settings-view.component"
							).then((m) => m.TagsSettingsViewComponent),

						children: {
							root: {
								pathMatch: "full",
								redirectTo: "orders_tags",
							},
							orders_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/orders-tags-settings-view/orders-tags-settings-view.component"
									).then((m) => m.OrdersTagsSettingsViewComponent),
							},
							pallets_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/pallets-tags-settings-view/pallets-tags-settings-view.component"
									).then((m) => m.PalletsTagsSettingsViewComponent),
							},
							articles_tags: {
								realtimeCollaboration: true,
								loadComponent: () =>
									import(
										"./modules/settings/subviews/articles-tags-settings-view/articles-tags-settings-view.component"
									).then((m) => m.ArticlesTagsSettingsViewComponent),
							},
						},
					},
				},
			},
			pickups: {
				showSidebar: true,
				canActivate: [AuthGuard, TenantAuthGuard],

				children: {
					root: {
						pathMatch: "full",
						redirectTo: "list",
					},
					list: {
						loadComponent: () =>
							import(
								"./modules/planning_office/pickups/views/pickups-list-view/pickups-list-view.component"
							).then((m) => m.PickupsListViewComponent),
					},
					create: {
						loadComponent: () =>
							import(
								"./modules/planning_office/pickups/views/create-edit-pickup-view/create-edit-pickup-view.component"
							).then((m) => m.CreateEditPickupViewComponent),
					},
					edit: {
						path: ":id/edit",
						loadComponent: () =>
							import(
								"./modules/planning_office/pickups/views/create-edit-pickup-view/create-edit-pickup-view.component"
							).then((m) => m.CreateEditPickupViewComponent),
					},
				},
			},
		},
	},
	// settings: {
	// 	showSidebar: true,
	// 	features: ["settings"],
	// 	canActivate: [AuthGuard, TenantAuthGuard],
	// 	loadComponent: () =>
	// 		import(
	// 			"./modules/settings/views/settings-view/settings-view.component"
	// 		).then((m) => m.SettingsViewComponent),
	// 	children: {
	// 		root: {
	// 			pathMatch: "full",
	// 			redirectTo: "user",
	// 		},
	// 		tenant: {
	// 			realtimeCollaboration: false,
	// 			loadComponent: () =>
	// 				import(
	// 					"./modules/settings/subviews/tenant-settings-view/tenant-settings-view.component"
	// 				).then((m) => m.TenantSettingsViewComponent),
	// 		},
	// 		user: {
	// 			realtimeCollaboration: false,
	// 			loadComponent: () =>
	// 				import(
	// 					"./modules/settings/subviews/user-settings-view/user-settings-view.component"
	// 				).then((m) => m.UserSettingsViewComponent),
	// 		},
	// 		collaborators: {
	// 			realtimeCollaboration: true,
	// 			loadComponent: () =>
	// 				import(
	// 					"./modules/settings/subviews/collaborators-settings-view/collaborators-settings-view.component"
	// 				).then((m) => m.CollaboratorsSettingsViewComponent),
	// 		},
	// 		price_list: {
	// 			realtimeCollaboration: false,
	// 			children: {
	// 				root: {
	// 					pathMatch: "full",
	// 					redirectTo: "list",
	// 				},
	// 				list: {
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/warehouse-price-list-settings-view/warehouse-price-list-settings-view.component"
	// 						).then((m) => m.WarehousePriceListSettingsViewComponent),
	// 				},
	// 				create: {
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
	// 						).then((m) => m.CreateEditPriceListComponent),
	// 				},
	// 				edit: {
	// 					path: ":id/edit",
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/create-edit-price-list/create-edit-price-list.component"
	// 						).then((m) => m.CreateEditPriceListComponent),
	// 				},
	// 			},
	// 		},
	// 		tags: {
	// 			realtimeCollaboration: true,
	// 			loadComponent: () =>
	// 				import(
	// 					"./modules/settings/subviews/tags-settings-view/tags-settings-view.component"
	// 				).then((m) => m.TagsSettingsViewComponent),

	// 			children: {
	// 				root: {
	// 					pathMatch: "full",
	// 					redirectTo: "orders_tags",
	// 				},
	// 				orders_tags: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/orders-tags-settings-view/orders-tags-settings-view.component"
	// 						).then((m) => m.OrdersTagsSettingsViewComponent),
	// 				},
	// 				pallets_tags: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/pallets-tags-settings-view/pallets-tags-settings-view.component"
	// 						).then((m) => m.PalletsTagsSettingsViewComponent),
	// 				},
	// 				articles_tags: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/settings/subviews/articles-tags-settings-view/articles-tags-settings-view.component"
	// 						).then((m) => m.ArticlesTagsSettingsViewComponent),
	// 				},
	// 			},
	// 		},
	// 		invoice_settings: {
	// 			realtimeCollaboration: true,
	// 			loadComponent: () =>
	// 				import(
	// 					"./modules/administration_office/settings/sections/invoice-settings-view/invoice-settings-view.component"
	// 				).then((m) => m.InvoiceSettingsViewComponent),

	// 			children: {
	// 				root: {
	// 					pathMatch: "full",
	// 					redirectTo: "payment_conditions",
	// 				},
	// 				payment_conditions: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/payment-conditions-view/payment-conditions-view.component"
	// 						).then((m) => m.PaymentConditionsViewComponent),
	// 				},
	// 				vat_codes: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/vat-codes-list-view/vat-codes-list-view.component"
	// 						).then((m) => m.VatCodesListViewComponent),
	// 				},
	// 				measure_units: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/measure-units-list-view/measure-units-list-view.component"
	// 						).then((m) => m.MeasureUnitsListViewComponent),
	// 				},
	// 				tenant_invoice_parameters: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/tenant-invoice-parameters-list-view/tenant-invoice-parameters-list-view.component"
	// 						).then((m) => m.TenantInvoiceParametersListViewComponent),
	// 				},
	// 				document_types: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/document-types-list-view/document-types-list-view.component"
	// 						).then((m) => m.DocumentTypesListViewComponent),
	// 				},
	// 				bank_cash: {
	// 					realtimeCollaboration: true,
	// 					loadComponent: () =>
	// 						import(
	// 							"./modules/administration_office/settings/views/bank-cash-list-view/bank-cash-list-view.component"
	// 						).then((m) => m.BankCashListViewComponent),
	// 				},
	// 			},
	// 		},
	// 	},
	// },
} as const;
